import { ComponentRef, Injectable, Injector } from '@angular/core';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicBindableValue,
  IDynamicItemValue,
  isOperation,
  LogicOperator,
  Operation,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ResolveValuesService } from '@epicuro-next/platform/dynamic-value-resolving';
import { isPrimitive } from '@epicuro-next/utilities';
import { isObservable, Observable, of } from 'rxjs';

import {
  and,
  equal,
  greater,
  greaterEqual,
  inFunc,
  not,
  or,
  smaller,
  smallerEqual,
} from './operations';

export type OperatorFunction = (
  ...args: Observable<unknown>[]
) => Observable<boolean>;

@Injectable()
export class LogicOperationsService {
  constructor(
    private readonly dataProvidersResolver: DataProvidersResolver,
    private readonly resolveValuesService: ResolveValuesService,
  ) {}

  public performOperationCheck(
    operation: Operation,
    injector?: Injector,
    componentRef?: ComponentRef<unknown>,
  ): Observable<boolean> {
    const [logicOperator, value] = operation;

    const func = this.pickOperatorFunction(logicOperator);

    if (!func) {
      return of(false);
    }

    // This is to cover case of 'NOT' operation, where we have only one Operation after LogicOperator
    if (isOperation(value)) {
      return func(this.performOperationCheck(value, injector, componentRef));
    }

    const compares = value.map((a) => {
      return isOperation(a)
        ? this.performOperationCheck(a, injector, componentRef)
        : this.decideOperationValue(
            a as IDynamicItemValue,
            injector,
            componentRef,
          );
    });

    return func(...compares);
  }

  private pickOperatorFunction(
    logicOperator: keyof typeof LogicOperator,
  ): OperatorFunction | null {
    switch (logicOperator) {
      case LogicOperator.EQ:
        return equal;
      case LogicOperator.NOT:
        return not;
      case LogicOperator.GT:
        return greater;
      case LogicOperator.LT:
        return smaller;
      case LogicOperator.AND:
        return and;
      case LogicOperator.OR:
        return or;
      case LogicOperator.IN:
        return inFunc;
      case LogicOperator.GTEQ:
        return greaterEqual;
      case LogicOperator.LTEQ:
        return smallerEqual;

      default:
        return null;
    }
  }

  private decideOperationValue(
    value: IDynamicItemValue,
    injector?: Injector,
    componentRef?: ComponentRef<unknown>,
  ): Observable<unknown> {
    if (isPrimitive(value) || Array.isArray(value)) {
      return of(value);
    }

    if (isObservable(value)) {
      return value;
    }

    const { provider, property } = value as IDynamicBindableValue;

    if (!provider || !property) {
      return of(value);
    }

    if (componentRef !== undefined) {
      const overrideWhitelistCheck = true;

      const scope =
        (componentRef.instance as { __scope__?: object }).__scope__ ??
        (componentRef.instance as object);

      let resolvedValue = this.resolveValuesService.resolveValue(
        value,
        componentRef,
        { overrideWhitelistCheck },
        scope,
      );

      if (!isObservable(resolvedValue)) {
        resolvedValue = of(resolvedValue);
      }

      return resolvedValue as Observable<unknown>;
    }

    return this.useLegacyValueResolving(provider, property, injector);
  }

  private useLegacyValueResolving(
    provider: string,
    property: unknown,
    injector?: Injector,
  ): Observable<unknown> {
    // This is legacy and only here because DynamicOperationIfPipe cannot provide a ComponentRef.
    // Components should be refactored to no longer reference data providers (resolver) themselves.
    // The components should have their values resolved by component-resolver.service.ts directly.
    // If that is done, then the DynamicOperationIfPipe will no longer get any IDynamicBindableValues.
    const service = this.dataProvidersResolver.resolve(provider, injector);

    if (!service) {
      return of(null);
    }

    return service.read<unknown>(property);
  }
}
